import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, MapPin, Inbox, Bot, LayoutGrid, Star, ShieldCheck, Clock, Users, ArrowRight, CalendarCheck, FileText, CheckCircle2, Check, Calculator, TrendingDown, TrendingUp, PhoneCall, Zap, MessageSquare, Wrench } from 'lucide-react';
const BOOKING_URL = 'https://api.viridiaanalytics.com/widget/booking/hv5pIfS27zbJCMMGNnkJ';
const PHONE_DISPLAY = '(623) 300-0708';
const PHONE_TEL = 'tel:+16233000708';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/dBgonnHgNocdZqWCx2rT29/VA-Logo_(1).png';
const CHAMBER_BADGE = 'https://cdn.magicpatterns.com/uploads/8HQjTNAJm388sm9my7xsCp/Scotsscommerce.png';

// GHL inbound webhook -- primed by VEGA with a sample payload (HTTP 200, field
// mapping confirmed) for exactly the keys this form sends. Inbound webhooks
// don't take an API key; the URL itself is a write-only trigger endpoint, so
// it's fine in client source (2026-08-18 handoff).
const LEAD_WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/JLycxFbUVQFY3z4zPpCS/webhook-trigger/33bbaf7b-b55d-498b-bf1b-3f24efca5231';
const MISS_RATE = 0.25;
const WEEKS_PER_MONTH = 4.33;

/* Tracking split three ways (2026-08-18) -- the old single trackLead() fired on
   every CTA click, so Meta was optimizing toward "clicked something" instead of
   a real conversion. Only the webhook-confirmed form submit is a true Lead now;
   booking and phone intent get their own distinctly-named events -- matching
   FUNNEL-SPEC.md section 5's canonical names -- so Kevin can read the funnel
   honestly in Ads Manager. */
function trackWebhookLead() {
  const w = window as any;
  if (typeof w.fbq === 'function') w.fbq('track', 'Lead');
  if (typeof w.gtag === 'function') w.gtag('event', 'generate_lead');
}
function trackBookingOpened() {
  const w = window as any;
  if (typeof w.fbq === 'function') w.fbq('trackCustom', 'BookingOpened');
  if (typeof w.gtag === 'function') w.gtag('event', 'booking_click');
}
function trackCallTapped() {
  const w = window as any;
  if (typeof w.fbq === 'function') w.fbq('trackCustom', 'CallTapped');
  if (typeof w.gtag === 'function') w.gtag('event', 'call_tapped');
}

/* A lead vanishing silently is worse than an error (2026-08-18 handoff). The
   visitor still sees success either way -- this is the "make it visible to
   Kevin" half: a distinct GA4 event that never fires on a real success, plus a
   local backup so the raw lead isn't lost even if nobody sees the console. */
function flagLeadDeliveryFailure(reason: string, lead: Record<string, string>) {
  console.error('[lead-form] delivery failed:', reason, lead);
  const w = window as any;
  if (typeof w.gtag === 'function') w.gtag('event', 'lead_delivery_failed', {
    reason
  });
  try {
    const key = 'va_failed_leads';
    const backup = JSON.parse(localStorage.getItem(key) || '[]');
    backup.push({
      ...lead,
      reason,
      ts: new Date().toISOString()
    });
    localStorage.setItem(key, JSON.stringify(backup));
  } catch {
    /* localStorage unavailable -- console.error above is the only record left */
  }
}
function usd(n: number) {
  return '$' + Math.round(n).toLocaleString('en-US');
}

/* Shared scroll-reveal + reduced-motion primitives (2026-08-18, item 9/hard
   constraint: no animation library, no new dependency). Plain IntersectionObserver
   and matchMedia, a few lines each, reused wherever the page needs entrance motion
   -- the form, the case study cards, the lead-flow diagram. */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}
function useInViewOnce(threshold = 0.2) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, {
      threshold
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, threshold]);
  return [ref, inView] as const;
}
function BookCTA({
  label = 'Book Your Free 15-Minute Call',
  full = false
}: {
  label?: string;
  full?: boolean;
}) {
  return <a href={BOOKING_URL} onClick={trackBookingOpened} className={`group inline-flex items-center justify-center gap-2 rounded-md bg-emerald-500 px-7 py-4 text-sm font-bold text-black transition-colors hover:bg-emerald-400 ${full ? 'w-full' : ''}`} data-id="element-0" data-mp="vv6zskfxcpmh">
      {label}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" data-id="element-1" data-mp="1w581h02dkstl" />
    </a>;
}

/* Calling is the lower-friction ask for this audience -- contractors reach for the
   phone before they fill in a form. The number is the GHL line, so the AI picks up:
   the call itself is a live demo of the product. Tap intent is real but isn't a
   confirmed lead, so it fires its own event, not Lead -- name per FUNNEL-SPEC.md
   section 5 (2026-08-18). */
function CallCTA({
  full = false
}: {
  full?: boolean;
}) {
  return <a href={PHONE_TEL} onClick={trackCallTapped} className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-emerald-500/60 bg-transparent px-5 py-4 text-sm font-bold tracking-tight text-emerald-300 transition-colors hover:bg-emerald-500/10 ${full ? 'w-full' : ''}`} data-id="element-2" data-mp="2jsg5zfrzbxuk">
      <PhoneCall className="h-4 w-4 flex-none" data-id="element-3" data-mp="ygoifmziv88x" />
      Call {PHONE_DISPLAY}
    </a>;
}
function Eyebrow({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="font-eyebrow inline-block text-[11px] font-semibold uppercase text-emerald-500" data-id="element-4" data-mp="21s3y5625iuno">{children}</div>;
}
function GuaranteeBadge({
  big = false
}: {
  big?: boolean;
}) {
  return <div className={`inline-flex items-center gap-2 rounded-full border-2 border-emerald-500/60 bg-emerald-500/15 font-extrabold uppercase tracking-wide text-emerald-300 shadow-[0_0_40px_-10px_rgba(16,185,129,0.7)] ${big ? 'px-6 py-3 text-base' : 'px-5 py-2.5 text-sm'}`} data-id="element-5" data-mp="2at1ssxqp9ptl">
      <ShieldCheck className={big ? 'h-6 w-6' : 'h-5 w-5'} data-id="element-6" data-mp="2fs50kpy2ea59" /> 14-Day Money-Back Guarantee
    </div>;
}

/* Moved OUT of the hero (2026-08-12). It was the first thing a visitor met on
   mobile -- two empty inputs asking them to do math before they knew what this
   was. 125 landing views produced zero CTA clicks with it gating the top of the
   page. It stays as a tool for people who scroll, not as the entry ask. */
function CostCalculator() {
  const [avgJob, setAvgJob] = useState('');
  const [newCallsPerWeek, setNewCallsPerWeek] = useState('');
  const avg = parseFloat(avgJob.replace(/[^0-9.]/g, '')) || 0;
  const nc = parseFloat(newCallsPerWeek.replace(/[^0-9.]/g, '')) || 0;
  const missedPerMonth = Math.round(nc * WEEKS_PER_MONTH * MISS_RATE);
  const opportunity = missedPerMonth * avg;
  const hasEstimate = avg > 0 && nc > 0;
  return <div className="mx-auto mt-10 w-full max-w-lg rounded-2xl border border-hairline2 bg-inkAlt p-6 sm:p-8" data-id="element-7" data-mp="3d6cz6nj7zh8e">
      <div className="flex items-center gap-2" data-id="element-8" data-mp="343vbe4nx2gja">
        <Calculator className="h-4 w-4 text-emerald-500" data-id="element-9" data-mp="344n8xl12qoc7" />
        <div className="font-eyebrow text-[10px] font-semibold uppercase text-emerald-500" data-id="element-10" data-mp="2pf7jq9h6yqym">Do the math on your own shop</div>
      </div>
      <h3 className="mt-2 text-xl font-bold leading-snug text-white" data-id="element-11" data-mp="2uf3ls36bw8bj">See what missed calls are costing you.</h3>
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-12" data-mp="2gesx96shr5tj">
        <label className="block" data-id="element-13" data-mp="2kg4dilph6ep2">
          <span className="text-xs font-medium text-neutral-400" data-id="element-14" data-mp="2kfcvkdoswfh9">Average job value</span>
          <div className="mt-1 flex items-center rounded-md border border-hairline2 bg-ink px-3 focus-within:border-emerald-500/60" data-id="element-15" data-mp="30u2j0hhdk5h6">
            <span className="text-sm text-neutral-500" data-id="element-16" data-mp="2vug320a5wb4o">$</span>
            <input type="text" inputMode="numeric" value={avgJob} onChange={e => setAvgJob(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="2,500" aria-label="Average job value" className="w-full bg-transparent px-2 py-3 text-sm text-white placeholder-neutral-600 outline-none" data-id="element-17" data-mp="2qvwert83in95" />
          </div>
        </label>
        <label className="block" data-id="element-18" data-mp="2d4hvdlaa0k1k">
          <span className="text-xs font-medium text-neutral-400" data-id="element-19" data-mp="286pw267axvnc">New customer calls / week</span>
          <input type="text" inputMode="numeric" value={newCallsPerWeek} onChange={e => setNewCallsPerWeek(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="15" aria-label="New customer calls per week" className="mt-1 w-full rounded-md border border-hairline2 bg-ink px-4 py-3 text-sm text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-20" data-mp="237n1xgsv19u5" />
        </label>
      </div>

      {hasEstimate ? <div className="mt-6 rounded-xl border border-emerald-500/40 bg-emerald-500/[0.07] p-6 text-center" data-id="element-21" data-mp="dpye4ugmmmp1">
          <div className="flex items-center justify-center gap-2 text-emerald-400" data-id="element-22" data-mp="ipazfarunlpi">
            <TrendingDown className="h-4 w-4" data-id="element-23" data-mp="np75rgo0rp88" />
            <span className="text-xs font-semibold uppercase tracking-wide" data-id="element-24" data-mp="nnw2rj2eeli6">Walking out your door every month</span>
          </div>
          <div className="mt-2 text-5xl font-extrabold tracking-tight text-white sm:text-6xl" data-id="element-25" data-mp="3r5e18zwa6g5x">{usd(opportunity)}</div>
          <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-26" data-mp="3w3yz4so28nr7">
            That's ~<span className="font-bold text-white" data-id="element-27" data-mp="4pt9gs2ti2ek">{missedPerMonth} new-customer calls</span> you miss a month at {usd(avg)} a job. Even if you'd only close a third, that's still <span className="font-bold text-white" data-id="element-28" data-mp="33fjosjfvitm7">{usd(opportunity * 0.33)}/month</span> gone to voicemail.
          </p>
          <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2" data-id="element-29" data-mp="38cjugt8t9ucs">
            <BookCTA label="Book a call" full data-id="element-30" data-mp="2f6lsvmtd3n1p" />
            <CallCTA full data-id="element-31" data-mp="2k5ye634l4m26" />
          </div>
          <p className="mt-2 text-[11px] text-neutral-500" data-id="element-32" data-mp="2k4n6vtbxkw6b">Estimate, based on the industry-typical 1-in-4 missed-call rate.</p>
        </div> : <p className="mt-5 text-xs text-neutral-500" data-id="element-33" data-mp="2u4e1dv0hrb6e">Enter your two numbers to see the number nobody wants to see.</p>}
    </div>;
}

/* Third-party proof for the "no voicemail" argument (item 3, 2026-08-18 handoff).
   Kevin's point: not answering is what removes you from consideration, and that
   needs an outside source, not a Viridia assertion. Picked the three numbers that
   carry the argument -- the voicemail stat proves the sentence directly above it,
   the other two build the "admission to the shortlist" case -- rather than
   stacking all six the handoff supplied. Verified figures only, attributed inline. */
function ProofStats() {
  const stats = [{
    icon: Inbox,
    stat: '< 1 in 6',
    body: 'unanswered calls to a contractor ever becomes a voicemail'
  }, {
    icon: Users,
    stat: '85%',
    body: 'of homeowners contact three or fewer contractors before hiring'
  }, {
    icon: TrendingDown,
    stat: '~40%',
    body: 'say they rarely or never hire a contractor who missed their first call'
  }];
  return <div className="mt-8 rounded-xl border border-hairline2 bg-ink p-6 sm:p-8" data-id="element-34" data-mp="3i0itqss4tirp">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 sm:gap-4" data-id="element-35" data-mp="3d0ec2zfhmhzw">
        {stats.map(({
        icon: Icon,
        stat,
        body
      }) => <div key={body} className="flex items-start gap-3 sm:flex-col sm:items-start sm:gap-2" data-id="element-36" data-mp="388v6z56ispwk">
            <Icon className="mt-0.5 h-4 w-4 flex-none text-emerald-500 sm:mt-0" strokeWidth={2} data-id="element-37" data-mp="349e1wcv09urx" />
            <div data-id="element-38" data-mp="2zaauwmvgtehb">
              <div className="text-2xl font-extrabold text-white" data-id="element-39" data-mp="2zat04903v31d">{stat}</div>
              <p className="mt-0.5 text-xs leading-snug text-neutral-400" data-id="element-40" data-mp="2u93mclli0b9l">{body}</p>
            </div>
          </div>)}
      </div>
      <p className="mt-5 border-t border-hairline2 pt-4 text-[11px] leading-relaxed text-neutral-500" data-id="element-41" data-mp="2pbtxp85xo3mt">
        Source: Service Direct's 2022 survey of 559 homeowners, and Service Direct's 2022 Call Performance Report.
        A homeowner picks from a short list and decides fast. Answering the phone isn't customer service, it's admission to that list.
      </p>
    </div>;
}

/* The missing third door (2026-08-18 handoff). Every CTA on this page is
   "book a calendar slot" or "dial this number" -- both high commitment. Someone
   interested but not ready to talk to a human right now had no way to raise a
   hand and left no trace. This is that door: low friction, three fields, and it
   sits after the pain/calculator section so it never gates the hero -- gating
   the hero with a form is the exact mistake the 2026-08-12 comment documents.
   Given real presence 2026-08-18 (item 4): full-width card, its own heading,
   larger inputs, a glow treatment matching the page's premium sections, and a
   once-only scroll reveal instead of just appearing flat in a narrow column. */
function SendNumbersForm() {
  const [name, setName] = useState('');
  const [business, setBusiness] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [hp, setHp] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done'>('idle');
  const [ref, inView] = useInViewOnce(0.25);
  const reducedMotion = usePrefersReducedMotion();
  const revealed = reducedMotion || inView;
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'submitting') return;
    const lead = {
      name,
      business,
      phone,
      email,
      source: 'ai.viridiaanalytics.com missed-lead check',
      page: typeof window !== 'undefined' ? window.location.href : '',
      hp
    };

    // Honeypot: a human never fills this field. If it's non-empty, drop the
    // submit client-side without hitting the webhook -- the URL is public.
    if (hp) {
      setStatus('done');
      return;
    }
    setStatus('submitting');
    try {
      const res = await fetch(LEAD_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(lead)
      });
      if (res.ok) {
        trackWebhookLead();
      } else {
        flagLeadDeliveryFailure('HTTP ' + res.status, lead);
      }
    } catch (err) {
      flagLeadDeliveryFailure(err instanceof Error ? err.message : 'network error', lead);
    }
    // Visitor always sees the success state -- a failed delivery is Kevin's
    // problem to chase via the flagged event/backup, not the visitor's to retry.
    setStatus('done');
  }
  const cardBase = `relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-inkAlt p-8 shadow-[0_20px_60px_-30px_rgba(16,185,129,0.4)] transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] sm:p-10 ${revealed ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`;
  if (status === 'done') {
    return <div ref={ref} className={cardBase} data-id="element-42" data-mp="1ygrsyym9fzy0">
        <div className="pointer-events-none absolute inset-0 opacity-70" style={{
        background: 'radial-gradient(60% 80% at 50% 0%, rgba(15,176,125,0.10) 0%, rgba(10,10,10,0) 70%)'
      }} data-id="element-43" data-mp="1tgdkxgknspzz" />
        <div className="relative text-center" data-id="element-44" data-mp="1oiu6ognyod81">
          <CheckCircle2 className="mx-auto h-9 w-9 text-emerald-400" data-id="element-45" data-mp="1kgh1btd4mhbs" />
          <h2 className="mt-4 text-2xl font-bold text-white" data-id="element-46" data-mp="2h5exng0iynb2">Got it, we'll call you.</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-neutral-300" data-id="element-47" data-mp="2c6lh1epxyg6o">
            Kevin will call you within one business day with a straight read on what missed calls are costing your shop.
          </p>
          <a href={BOOKING_URL} onClick={trackBookingOpened} className="mt-5 inline-block text-xs font-semibold text-emerald-400 underline decoration-emerald-700 underline-offset-4 hover:text-emerald-300" data-id="element-48" data-mp="23f34dds7eiic">
            Or grab a time now instead
          </a>
        </div>
      </div>;
  }
  return <div ref={ref} className={cardBase} data-id="element-49" data-mp="t7nn10rnlujz">
      <div className="pointer-events-none absolute inset-0 opacity-70" style={{
      background: 'radial-gradient(60% 80% at 50% 0%, rgba(15,176,125,0.10) 0%, rgba(10,10,10,0) 70%)'
    }} data-id="element-50" data-mp="t8p71uqndrdu" />
      <div className="relative" data-id="element-51" data-mp="jhkv73w9qmn8">
        <div className="font-eyebrow text-[10px] font-semibold uppercase text-emerald-500" data-id="element-52" data-mp="jg9puzn6d6u0">Not ready to call?</div>
        <h2 className="mt-2 text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-53" data-mp="ahpfqmvok5he">Send me my numbers, I'll call you.</h2>
        <p className="mt-3 text-sm leading-relaxed text-neutral-400" data-id="element-54" data-mp="fgsk053u67ob">
          Three fields. Kevin calls you back within one business day with a straight read on what missed calls are costing your shop.
        </p>
        <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 gap-4" data-id="element-55" data-mp="5j4fingfhlsy">
          <input type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Your name" aria-label="Your name" autoComplete="name" className="w-full rounded-lg border border-hairline2 bg-ink px-5 py-4 text-base text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-56" data-mp="1ux16u7t1soey" />
          <input type="text" required value={business} onChange={e => setBusiness(e.target.value)} placeholder="Business name" aria-label="Business name" autoComplete="organization" className="w-full rounded-lg border border-hairline2 bg-ink px-5 py-4 text-base text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-57" data-mp="3fgmpho0yh01y" />
          <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone number" aria-label="Phone number" autoComplete="tel" className="w-full rounded-lg border border-hairline2 bg-ink px-5 py-4 text-base text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-58" data-mp="71cqwpw9ycyx" />
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email (optional)" aria-label="Email (optional)" autoComplete="email" className="w-full rounded-lg border border-hairline2 bg-ink px-5 py-4 text-base text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-59" data-mp="2ioj76v0868b6" />
          {/* Honeypot -- visually hidden from sighted users, off the tab order, still
              reachable by a bot filling every field it finds. */}
          <input type="text" name="hp" value={hp} onChange={e => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }} data-id="element-60" data-mp="26lovslg7vfph" />
          <button type="submit" disabled={status === 'submitting'} className="mt-1 w-full rounded-lg bg-emerald-500 px-7 py-4 text-base font-bold text-black transition-colors hover:bg-emerald-400 disabled:opacity-60" data-id="element-61" data-mp="1ivb10n68z2r7">
            {status === 'submitting' ? 'Sending…' : 'Send my numbers'}
          </button>
        </form>
        <p className="mt-4 text-center text-xs text-neutral-500" data-id="element-62" data-mp="2zcgqtfljpyb8">No spam, no drip campaign. Kevin calls you himself.</p>
      </div>
    </div>;
}

/* Case study section (item 6/7, 2026-08-18 handoff): the same two verified
   results the proof section used to carry as short blurbs, restructured as a
   story -- situation, what changed, the number -- because a story reads as
   evidence and a bare percentage reads as a claim. No client names, no
   screenshots, both per Kevin's standing anonymity rule.
   NOTE (2026-08-18, later same day): this section originally REPLACED the dare
   section. That was reversed -- Kevin confirmed the receptionist is reliable
   enough to hand to a cold stranger, so the dare is back above and these two now
   run together: the dare proves the product live, the case studies prove the
   outcome. Do not re-cut the dare without also cutting the hero's promise of it. */
function CaseStudyCard({
  eyebrow,
  situation,
  change,
  number
}: {
  eyebrow: string;
  situation: string;
  change: string;
  number: string;
}) {
  return <div className="rounded-lg border border-hairline2 bg-inkAlt p-6 sm:p-7" data-id="element-63" data-mp="2c6avddyti1dy">
      <div className="font-eyebrow text-[10px] font-semibold uppercase text-neutral-500" data-id="element-64" data-mp="277purdfnlfoz">{eyebrow}</div>
      <div className="mt-5 space-y-4" data-id="element-65" data-mp="22h11bskey8n7">
        <div className="flex gap-3" data-id="element-66" data-mp="22fyebvq7oqck">
          <Wrench className="mt-0.5 h-4 w-4 flex-none text-neutral-500" strokeWidth={2} data-id="element-67" data-mp="1xig1hu92pqza" />
          <p className="text-sm leading-relaxed text-neutral-400" data-id="element-68" data-mp="1xglpj5fs597c"><span className="font-semibold text-neutral-300" data-id="element-69" data-mp="1thmfj6f6ym91">The situation. </span>{situation}</p>
        </div>
        <div className="flex gap-3" data-id="element-70" data-mp="1og6tpc56wbpl">
          <Zap className="mt-0.5 h-4 w-4 flex-none text-emerald-500" strokeWidth={2} data-id="element-71" data-mp="1jiou4cd2b8u9" />
          <p className="text-sm leading-relaxed text-neutral-400" data-id="element-72" data-mp="1jki1xuzevz3u"><span className="font-semibold text-neutral-300" data-id="element-73" data-mp="1ekna9p7tb5hq">What changed. </span>{change}</p>
        </div>
        <div className="flex gap-3" data-id="element-74" data-mp="3dveoev7cek26">
          <TrendingUp className="mt-0.5 h-4 w-4 flex-none text-emerald-500" strokeWidth={2} data-id="element-75" data-mp="3dubyaphuglnb" />
          <p className="text-sm leading-relaxed text-white" data-id="element-76" data-mp="38vigikviy8hc"><span className="font-semibold" data-id="element-77" data-mp="3hff3nigwciv5">The number. </span>{number}</p>
        </div>
      </div>
    </div>;
}
function CaseStudies() {
  const [ref, inView] = useInViewOnce(0.15);
  const reducedMotion = usePrefersReducedMotion();
  const revealed = reducedMotion || inView;
  const cards = [{
    eyebrow: 'Phoenix landscaping company',
    situation: 'A two-truck crew was missing about 1 in 4 calls, business as usual when there is more work than hands to answer the phone.',
    change: 'The AI answered every call and Yelp message instantly, day and night, and booked estimates straight onto the calendar without anyone touching a phone.',
    number: '284% more leads on the same ad spend, 28 estimates booked end to end by the AI, and a 39.95% close rate. Response time went from 4+ hours to instant.'
  }, {
    eyebrow: 'Residential and commercial painting company',
    situation: 'Close rate was strong whenever they actually reached a lead. The problem was reaching them before someone else did.',
    change: 'The AI took first response off their plate and booked its own estimates while the owner stayed on the tools running the jobs he already had.',
    number: '29 estimates booked in 30 days, about one a day, and $15,050 closed from estimates the AI booked hands-free.'
  }];
  return <section className="border-b border-hairline bg-inkAlt" data-id="element-78" data-mp="6y66wzpe57h8">
      <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-79" data-mp="3fh3ymvvlueyg">
        <Eyebrow data-id="element-80" data-mp="3agzfey3bb64j">What this looks like for a shop like yours</Eyebrow>
        <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-81" data-mp="3aisn8gpnvwe4">
          Two real shops, before and after.
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-82" data-mp="3kevqjpw9212n">
          Real past-client outcomes, anonymized. Not a guarantee of the same numbers for every business.
        </p>
        <div ref={ref} className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-83" data-mp="2rr9own6l16if">
          {cards.map((c, i) => <div key={c.eyebrow} className="transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]" style={{
          transitionDelay: revealed ? `${i * 150}ms` : '0ms',
          opacity: revealed ? 1 : 0,
          transform: revealed ? 'translateY(0)' : 'translateY(24px)'
        }} data-id="element-84" data-mp="177e98zf7png8" data-mpi={i}>
              <CaseStudyCard {...c} data-id="element-85" data-mp="1pyf9rrvzdr1w" />
            </div>)}
        </div>
      </div>
    </section>;
}
const FLOW_SOURCES = ['Missed call', 'Yelp', 'Google', 'Facebook', 'Web form'];
const FLOW_STAGES = [{
  icon: Inbox,
  label: 'Lead captured',
  sub: 'Every call & message'
}, {
  icon: CalendarCheck,
  label: 'Booked',
  sub: 'On your calendar'
}, {
  icon: FileText,
  label: 'Invoice sent',
  sub: 'QuickBooks synced'
}, {
  icon: CheckCircle2,
  label: 'Job completed',
  sub: 'Tracked to done'
}, {
  icon: Star,
  label: 'Review requested',
  sub: 'Your reputation grows'
}];

/* Depth on the nodes (item 5, 2026-08-18): a soft directional glow + inset
   highlight instead of a flat fill, real easing instead of a linear transition. */
function FlowNode({
  stage,
  state
}: {
  stage: (typeof FLOW_STAGES)[number];
  state: 'idle' | 'current' | 'done';
}) {
  const Icon = stage.icon;
  const done = state === 'done';
  const current = state === 'current';
  return <div className="flex w-28 flex-none flex-col items-center text-center sm:w-32" data-id="element-86" data-mp="b0uiso648aor">
      <div className="relative" data-id="element-87" data-mp="18pweu9juoo41">
        {current && <span className="absolute inset-0 rounded-full bg-emerald-500/30 animate-[vaPing_1.6s_ease-out_infinite]" data-id="element-88" data-mp="13r31qi8h6z4e" />}
        <div className={`relative flex h-12 w-12 items-center justify-center rounded-full border transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${done ? 'border-emerald-500 bg-gradient-to-b from-emerald-400 to-emerald-600 text-black shadow-[0_4px_16px_-2px_rgba(16,185,129,0.55),inset_0_1px_0_rgba(255,255,255,0.3)]' : current ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400 shadow-[0_0_0_5px_rgba(16,185,129,0.08)]' : 'border-hairline2 bg-inkAlt text-neutral-600'}`} data-id="element-89" data-mp="yrgnq6ktpeuj">
          {done ? <Check className="h-5 w-5" strokeWidth={2.5} data-id="element-90" data-mp="23yq9isbzfw5j" /> : <Icon className="h-5 w-5" strokeWidth={2} data-id="element-91" data-mp="23xydjgeh3weq" />}
        </div>
      </div>
      <div className={`mt-3 text-sm font-semibold transition-colors duration-500 ${done || current ? 'text-white' : 'text-neutral-500'}`} data-id="element-92" data-mp="28rstc8qyjr8j">{stage.label}</div>
      <div className={`mt-0.5 text-[11px] leading-tight transition-colors duration-500 ${done || current ? 'text-neutral-400' : 'text-neutral-600'}`} data-id="element-93" data-mp="2inw76bv69ebb">{stage.sub}</div>
    </div>;
}

/* Travelling pulse instead of a hard fill (item 5): the track fills with a
   smoother, longer ease, and a small glowing dot travels along it once as it
   fills, then fades. Pure CSS keyframes, no library. */
function FlowConnector({
  filled,
  pulsing
}: {
  filled: boolean;
  pulsing: boolean;
}) {
  return <div className="flex items-center justify-center py-1 md:flex-1 md:px-1 md:py-0" data-id="element-94" data-mp="20lqe9htzhocw">
      <div className="relative h-7 w-px overflow-hidden rounded-full bg-hairline2 md:mt-6 md:h-px md:w-full" data-id="element-95" data-mp="1vnf579bijhvq">
        <div className={`absolute inset-0 origin-top rounded-full bg-gradient-to-b from-emerald-400 to-emerald-500 transition-transform duration-[750ms] ease-[cubic-bezier(0.22,1,0.36,1)] md:origin-left md:bg-gradient-to-r ${filled ? 'scale-y-100 md:scale-x-100' : 'scale-y-0 md:scale-x-0'}`} data-id="element-96" data-mp="29ng6gpvowrbq" />
        {pulsing && <>
            <span className="va-pulse-dot va-pulse-dot-v md:hidden" aria-hidden="true" data-id="element-97" data-mp="2sdepg11104u5" />
            <span className="va-pulse-dot va-pulse-dot-h hidden md:block" aria-hidden="true" data-id="element-98" data-mp="2sf8zgkarecjg" />
          </>}
      </div>
    </div>;
}

/* Runs once on entering the viewport instead of looping forever (item 5): an
   IntersectionObserver starts a single staggered play-through the first time the
   diagram scrolls into view, then stops. Reduced motion jumps straight to the
   finished state, same as before. */
function LeadFlow() {
  const [active, setActive] = useState(-1);
  const [pulseIndex, setPulseIndex] = useState(-1);
  const [started, setStarted] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  useEffect(() => {
    const el = containerRef.current;
    if (!el || started) return;
    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        setStarted(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.35
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    if (reducedMotion) {
      setActive(FLOW_STAGES.length - 1);
      return;
    }
    const timers = FLOW_STAGES.map((_, i) => setTimeout(() => {
      setPulseIndex(i - 1);
      setActive(i);
    }, i * 550));
    const clearPulse = setTimeout(() => setPulseIndex(-1), FLOW_STAGES.length * 550 + 750);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(clearPulse);
    };
  }, [started, reducedMotion]);
  const capturing = active === 0;
  return <div ref={containerRef} className="relative mt-10 overflow-hidden rounded-xl border border-hairline2 bg-inkAlt p-6 sm:p-10" data-id="element-99" data-mp="2grpansffudrk">
      <style data-id="element-100" data-mp="2byw9fej6hnk8">{`
        @keyframes vaPing{0%{transform:scale(1);opacity:.5}70%,100%{transform:scale(2);opacity:0}}
        @keyframes vaPulseV{0%{transform:translateY(-100%);opacity:0}15%{opacity:1}85%{opacity:1}100%{transform:translateY(700%);opacity:0}}
        @keyframes vaPulseH{0%{transform:translateX(-100%);opacity:0}15%{opacity:1}85%{opacity:1}100%{transform:translateX(700%);opacity:0}}
        @keyframes vaGlow{0%,100%{opacity:.4}50%{opacity:.75}}
        .va-pulse-dot{position:absolute;width:10px;height:10px;border-radius:9999px;background:radial-gradient(circle,rgba(52,211,153,.95) 0%,rgba(52,211,153,0) 72%);pointer-events:none}
        .va-pulse-dot-v{left:50%;top:0;margin-left:-5px;animation:vaPulseV 700ms cubic-bezier(0.22,1,0.36,1) both}
        .va-pulse-dot-h{top:50%;left:0;margin-top:-5px;animation:vaPulseH 700ms cubic-bezier(0.22,1,0.36,1) both}
        .va-glow{animation:vaGlow 6s ease-in-out infinite}
        @media (prefers-reduced-motion: reduce){.va-pulse-dot{animation:none;display:none}.va-glow{animation:none}}
      `}</style>
      <div className="va-glow pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(55% 60% at 50% 38%, rgba(15,176,125,0.12) 0%, rgba(10,10,10,0) 72%)'
    }} data-id="element-101" data-mp="3uyn7umsycmlw" />
      <div className="relative" data-id="element-102" data-mp="32ahmpyxzkvlj">
        <div className="font-eyebrow text-center text-[10px] font-semibold uppercase text-neutral-500" data-id="element-103" data-mp="329g8xmpl5v04">Leads come in from</div>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2" data-id="element-104" data-mp="377q43xbb8vlq">
          {FLOW_SOURCES.map(s => <span key={s} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-500 ${capturing ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-hairline2 bg-ink text-neutral-500'}`} data-id="element-105" data-mp="2gxua5405mw0e">
              <span className={`h-1.5 w-1.5 rounded-full transition-colors duration-500 ${capturing ? 'bg-emerald-400' : 'bg-neutral-600'}`} data-id="element-106" data-mp="2byzjjfvy435q" />{s}
            </span>)}
        </div>
        <div className="mx-auto mt-3 h-5 w-px bg-gradient-to-b from-emerald-500/60 to-transparent" data-id="element-107" data-mp="230nqz8biqvan" />
        <div className="mt-2 flex flex-col items-center md:flex-row md:items-start md:justify-center" data-id="element-108" data-mp="1ockmy4h1dtoh">
          {FLOW_STAGES.map((stage, i) => {
          const state: 'idle' | 'current' | 'done' = active > i ? 'done' : active === i ? 'current' : 'idle';
          return <React.Fragment key={stage.label} data-id="element-109" data-mp="1k8cvw0n3w1n3" data-mpi={i}>
                <FlowNode stage={stage} state={state} data-id="element-110" data-mp="1kb7f5oudvgex" />
                {i < FLOW_STAGES.length - 1 && <FlowConnector filled={active > i} pulsing={pulseIndex === i} data-id="element-111" data-mp="10pp4404enmb0" />}
              </React.Fragment>;
        })}
        </div>
        <div className="mt-8 text-center text-xs text-neutral-500" data-id="element-112" data-mp="19oj6k74f36wh">One system runs the whole job, start to finish. You just show up and do the work.</div>
      </div>
    </div>;
}

/* THE DARE -- restored 2026-08-18 after Kevin confirmed the receptionist is
   reliable enough to hand to a cold stranger. It was cut earlier the same day
   (the case-study section took its slot) while the hero kept promising it:
   "Call right now and our AI will answer. That's the whole product, live."
   That left the page making a claim it no longer backed.
   Restored verbatim from the version that was actually live (artifact aa891d60,
   v10), NOT from v9 -- v9's test-call pull-quote was deliberately removed in
   v10 and stays removed. Only change: the CTA fires trackCallTapped, not the
   old trackLead, so a dare tap stays honest intent and never counts as a
   converted Lead. */
function TheDare() {
  return <section className="relative overflow-hidden border-b border-hairline bg-inkAlt" data-id="element-113" data-mp="100lgi0xenn8c">
      <div className="pointer-events-none absolute inset-0 opacity-70" style={{
      background: 'radial-gradient(60% 70% at 50% 50%, rgba(15,176,125,0.14) 0%, rgba(10,10,10,0) 70%)'
    }} data-id="element-114" data-mp="140vm3chacbmo" />
      <div className="relative mx-auto max-w-3xl px-5 py-16 text-center sm:py-24" data-id="element-115" data-mp="1drgkozwylg5l">
        <Eyebrow data-id="element-116" data-mp="1iq1kwzc7nzu1">Don't take our word for it</Eyebrow>
        <h2 className="mt-4 text-3xl font-extrabold leading-[1.15] tracking-tight text-white sm:text-4xl" data-id="element-117" data-mp="1iogqratt6iwp">
          Think AI won't close?<br className="hidden sm:block" data-id="element-118" data-mp="1nna5t7oquhyz" /> Call it right now.
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-neutral-300" data-id="element-119" data-mp="1rq6jql3c9pwl">
          The number below isn't a sales line. It's the exact system we'd put on your phone,
          answering live. Try to stump it. Ask what it costs. Ask if it's a robot.
        </p>

        <div className="mx-auto mt-8 max-w-md" data-id="element-120" data-mp="3lzlovxcxg12m">
          <a href={PHONE_TEL} onClick={trackCallTapped} className="group flex w-full items-center justify-center gap-3 rounded-md bg-emerald-500 px-7 py-5 text-lg font-extrabold tracking-tight text-black transition-colors hover:bg-emerald-400" data-id="element-121" data-mp="3m0eqyl0xy2ef">
            <PhoneCall className="h-5 w-5 flex-none" data-id="element-122" data-mp="3852mhlwzgxkb" />
            Call {PHONE_DISPLAY}
          </a>
          <p className="mt-3 text-xs text-neutral-500" data-id="element-123" data-mp="8po271h5mllj">
            Answers in seconds, 24/7, same as it would for your customers.
          </p>
        </div>

        <p className="mx-auto mt-10 max-w-lg text-sm leading-relaxed text-neutral-400" data-id="element-124" data-mp="3vvf09dewtoit">
          That's what picks up at 9pm when you're covered in drywall dust. Not voicemail.
        </p>
      </div>
    </section>;
}
export function App() {
  return <div className="min-h-full w-full bg-ink text-neutral-100" data-id="element-125" data-mp="2fihvwnoc0pht">
      {/* 1. HERO -- reverted 2026-08-18 (Kevin's own review, overruling the prior
             "$15,050" hero). His reasoning: this buyer has already heard every
             money-back, cancel-anytime pitch there is, so the first thing on the
             page has to disarm, not impress. Subhead tuned to risk removal, not
             capability. Message-match: RESOLVED 2026-08-18 -- Kevin activated the
             paused "Ad - pain" ad, whose headline matches this H1 verbatim, so the
             ad and the page now say the same thing. "Ad - stat proof ($15,050)"
             remains active and does NOT match this H1; if it stays on, that half
             of the spend is still mismatched. */}
      <header className="relative overflow-hidden border-b border-hairline" data-id="element-126" data-mp="h1php4dw14hu">
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{
        background: 'radial-gradient(60% 50% at 50% 0%, rgba(15,176,125,0.16) 0%, rgba(10,10,10,0) 70%)'
      }} data-id="element-127" data-mp="lx4s1mcosi9i" />
        <div className="relative mx-auto flex max-w-3xl flex-col items-center px-5 pb-14 pt-10 text-center sm:pb-18 sm:pt-14" data-id="element-128" data-mp="36pyfra1svnz7">
          <img src={LOGO_URL} alt="Viridia Analytics" className="h-16 w-auto sm:h-24" data-id="element-129" data-mp="2y63ccqzuvdov" />
          <div className="mt-8" data-id="element-130" data-mp="2u5rq59ruh5ba"><Eyebrow data-id="element-131" data-mp="2u8dxp9eqjkti">Phoenix Home-Service Owners</Eyebrow></div>
          <h1 className="mt-4 text-[2.2rem] font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl" data-id="element-132" data-mp="38514e3whppcm">
            We answer every lead, or you don't pay.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-400 sm:text-lg" data-id="element-133" data-mp="2fgn8pusvj548">
            Every call, text, and Yelp lead answered within seconds, day or night. If one slips through
            during your first 14 days, that month is on us. No fine print, no hoops to jump through.
          </p>

          <div className="mt-8 grid w-full max-w-md grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-134" data-mp="2kfgmloc2oy4x">
            <BookCTA label="Book Your Free 15-Minute Call" full data-id="element-135" data-mp="1wq5h5tjs2fkg" />
            <CallCTA full data-id="element-136" data-mp="1rru99od1mf4v" />
          </div>
          <p className="mt-3 text-xs text-neutral-500" data-id="element-137" data-mp="1rqsyzm3upwo7">
            Call right now and our AI will answer. That's the whole product, live.
          </p>

          <div className="mt-7" data-id="element-138" data-mp="nsp7ztdyh5z"><GuaranteeBadge big data-id="element-139" data-mp="n95cdk6dipb" /></div>
        </div>
      </header>

      {/* 2. THE PAIN */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-140" data-mp="5er8n89o7vdg">
        <div className="mx-auto max-w-4xl px-5 py-14 sm:py-20" data-id="element-141" data-mp="5gltynoutpi2">
          <Eyebrow data-id="element-142" data-mp="9f1d2ghtedq7">What a missed call actually costs you</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-143" data-mp="38vhglsotcihs">
            The caller doesn't leave a voicemail. They just call the next name on Google.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-144" data-mp="3hw5vsqpy0ow9">
            A busy shop misses 1 out of every 4 calls. At a few thousand dollars a job, that's tens of
            thousands walking out the door every month, leads you already paid Google, Yelp, and Meta to send you.
          </p>
          <ProofStats data-id="element-145" data-mp="123yuyii468if" />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-146" data-mp="123gn0om3a5uo">
            {["You're on a rooftop swapping a condenser and the phone buzzes in your pocket.", "You're under a sink mid-repair with wet hands and no way to grab it.", "You're driving between jobs and it goes straight to voicemail.", "You're mid-estimate with one customer while a second lead is calling right now."].map((line, i) => <div key={i} className="rounded-lg border border-hairline bg-ink p-5 text-sm leading-relaxed text-neutral-400" data-id="element-147" data-mp="tigz8lsl7edu" data-mpi={i}>{line}</div>)}
          </div>
          <CostCalculator data-id="element-148" data-mp="jk1b8rouwl8k" />
        </div>
      </section>

      {/* 2b. THE THIRD DOOR */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-149" data-mp="jlugc2jtmxeg">
        <div className="mx-auto max-w-xl px-5 py-16 sm:py-24" data-id="element-150" data-mp="fk0kq8ohgjl5">
          <SendNumbersForm data-id="element-151" data-mp="2dykc6ptr2goh" />
        </div>
      </section>

      {/* 3. THE FIX */}
      <section className="border-b border-hairline" data-id="element-152" data-mp="29wqemqeupssj">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-153" data-mp="29wyr4h72aw37">
          <Eyebrow data-id="element-154" data-mp="2t4yoe505l3qu">From missed call to five-star review</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-155" data-mp="2t7kxi92ozrb6">
            One lead comes in. The system runs the entire job for you.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-156" data-mp="2jdb3yo20kmyv">
            Every call, text, and message from every platform lands in one place, and the system carries
            it the whole way: booked, invoiced, completed, and reviewed. Nothing slips.
          </p>
          <LeadFlow data-id="element-157" data-mp="2ehm0waqscnxt" />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-158" data-mp="2edy6zahfhzik">
            {[{
            icon: Bot,
            title: 'Instant response to every lead',
            body: 'An AI receptionist answers every call, text, and message in seconds, 24/7, before they dial the next name.'
          }, {
            icon: Inbox,
            title: 'One unified inbox',
            body: 'Every call, text, Yelp, Google, and Facebook message lands in one place, not five apps.'
          }, {
            icon: LayoutGrid,
            title: 'Branded CRM + mobile app',
            body: 'Run the whole thing from your phone. It chases every lead until they book or say no.'
          }, {
            icon: Star,
            title: 'Reviews + QuickBooks-synced invoicing',
            body: 'Reputation and invoicing run in the background while you run the job. Google Business Profile set up and optimized.'
          }].map(({
            icon: Icon,
            title,
            body
          }, i) => <div key={i} className="rounded-lg border border-hairline2 bg-inkAlt p-6" data-id="element-159" data-mp="whebfwiqgc8l" data-mpi={i}>
                <Icon className="h-5 w-5 text-emerald-500" strokeWidth={2} data-id="element-160" data-mp="rijiw2uyr9ba" />
                <h3 className="mt-4 text-base font-semibold text-white" data-id="element-161" data-mp="15jvygd67p8wn">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-162" data-mp="11hsjvfo8xo75">{body}</p>
              </div>)}
          </div>
          <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-163" data-mp="37bs81h8tk8os">
            <BookCTA label="Book a 15-Min Call" data-id="element-164" data-mp="33a708ab56klg" />
            <CallCTA data-id="element-165" data-mp="33bi387wrjobi" />
          </div>
        </div>
      </section>

      {/* 3b. THE DARE -- restored. Sits right after the mechanism, where
             skepticism peaks: the visitor has just been told what the system
             does, so this is the moment to hand them the phone instead of
             arguing. */}
      <TheDare data-id="element-166" data-mp="1nhj75ubwcdwl" />

      {/* 3c. CASE STUDIES -- the dare proves the product works live, these prove
             it produced an outcome. Both, not either. */}
      <CaseStudies data-id="element-167" data-mp="21f7sf39vln26" />

      {/* 4. GUARANTEE -- now names the mechanisms explicitly (item 2,
             2026-08-18), so there's no ambiguity about what's covered. Scope
             (response speed only, timestamped and logged) and the delivery
             carve-out stay exactly as they were. */}
      <section className="border-b border-hairline" data-id="element-168" data-mp="268kdx4j9ddqp">
        <div className="mx-auto max-w-3xl px-5 py-14 text-center sm:py-20" data-id="element-169" data-mp="2k70himltqo4m">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/10" data-id="element-170" data-mp="2f8h08zi6e0il">
            <ShieldCheck className="h-7 w-7 text-emerald-500" data-id="element-171" data-mp="2yvaaeaqnme7a" />
          </div>
          <h2 className="mt-5 text-2xl font-extrabold leading-snug text-white sm:text-3xl" data-id="element-172" data-mp="33tvbsdhn73xb">
            We respond to every missed call in your first 14 days, or your first month is 100% money back.
          </h2>
          <div className="mx-auto mt-6 grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-3" data-id="element-173" data-mp="2tu4jmigk110e">
            {[{
            icon: Zap,
            text: 'Auto-responders fire for every lead'
          }, {
            icon: Bot,
            text: 'AI receptionist configured for missed calls'
          }, {
            icon: MessageSquare,
            text: 'Missed-call text-back'
          }].map(({
            icon: Icon,
            text
          }) => <div key={text} className="flex items-center gap-2.5 rounded-lg border border-hairline2 bg-inkAlt p-4 sm:flex-col sm:items-center sm:gap-2 sm:text-center" data-id="element-174" data-mp="2b44xo4g0g217">
                <Icon className="h-4 w-4 flex-none text-emerald-500 sm:h-5 sm:w-5" strokeWidth={2} data-id="element-175" data-mp="1x79cxeqecm5b" />
                <span className="text-xs font-medium leading-snug text-neutral-300" data-id="element-176" data-mp="225sy3cx61twm">{text}</span>
              </div>)}
          </div>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-neutral-400" data-id="element-177" data-mp="2w03lztraxhw">
            We guarantee the one thing we fully control: missed-call response speed. Every response is
            timestamped and logged. If our system does not respond to every missed call within 5 minutes
            during your first 14 days, your first month is refunded, no hassle. See full terms below.
          </p>
        </div>
      </section>

      {/* 5. WHO'S BEHIND IT -- trimmed 2026-08-18: the two client results now
             live in the case study section above as full stories, so this section
             carries the credibility line, trust badges, and CTA without repeating
             the same two numbers a second time. */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-178" data-mp="32a3h0bkufllc">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-179" data-mp="2xc230529ycgq">
          <Eyebrow data-id="element-180" data-mp="1cvb0432fqxgt">Who's behind this</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-181" data-mp="1cwm2byg8fx5t">
            Run by people who've done this before, not a call center script.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-182" data-mp="1luxonoa2g8sg">
            Built by former Yelp reps who managed over $2 million a month in ad spend for home-service businesses.
          </p>

          <div className="mt-10 flex flex-col items-center gap-4" data-id="element-183" data-mp="1vl0q8cv96v2e">
            <div className="flex flex-col gap-3 sm:flex-row" data-id="element-184" data-mp="20ju80hhkp88d">
              <BookCTA label="Get this on your calendar" data-id="element-185" data-mp="20mgesewn3jpj" />
              <CallCTA data-id="element-186" data-mp="25l1dub05nxce" />
            </div>
            <div className="flex flex-wrap items-center gap-6 opacity-90" data-id="element-187" data-mp="29lbjtnny6nr9">
              <img src={CHAMBER_BADGE} alt="Scottsdale Chamber of Commerce" className="h-9 w-auto" data-id="element-188" data-mp="29kijp5jhuwi3" />
            </div>
          </div>
        </div>
      </section>

      {/* 6. OFFER -- item 8's promotional-rate change was cancelled by Kevin the
             same day (commit f072d19, "dont do the 897 thing") before this run
             read the handoff. Pricing stays exactly as it was: plain $1,197/month,
             no strikethrough, no founding-rate framing, no scarcity language
             attached to price. The 5-businesses-a-month line stays a capacity
             statement further down, not a pricing deadline. */}
      <section className="border-b border-hairline" data-id="element-189" data-mp="2xc0fg7j0ic1n">
        <div className="mx-auto max-w-2xl px-5 py-14 text-center sm:py-20" data-id="element-190" data-mp="2xcju9fdd8kbg">
          <Eyebrow data-id="element-191" data-mp="2sdyufh2102nj">The offer</Eyebrow>
          <h2 className="mt-3 text-4xl font-extrabold text-white sm:text-5xl" data-id="element-192" data-mp="2saui1weeq0ls">$1,197<span className="text-xl font-semibold text-neutral-400" data-id="element-193" data-mp="372liq89ms4qw">/month</span></h2>
          <p className="mt-2 text-sm font-medium text-emerald-500" data-id="element-194" data-mp="32a273gui7joq">Month to month. No contract. Cancel anytime.</p>

          <div className="mx-auto mt-7 max-w-md rounded-xl border border-hairline2 bg-inkAlt p-6 text-left" data-id="element-195" data-mp="2ek7tgx2d1r3f">
            <p className="text-center text-sm font-semibold text-white" data-id="element-196" data-mp="29lwigj0bxajm">Why that number is a steal:</p>
            <ul className="mt-4 space-y-2.5" data-id="element-197" data-mp="p32jbf8qv66j">
              {['Replaces a full-time receptionist ($3,000+/mo). The AI answers every call, 24/7.', 'Replaces your CRM, review tool, and invoicing. One bill instead of five apps.', 'Catches the calls you already paid Google, Yelp & Meta to send you.', 'Built and set up with you, in your voice. Live in days, not months.'].map(li => <li key={li} className="flex items-start gap-2 text-sm leading-relaxed text-neutral-300" data-id="element-198" data-mp="1cswvrvp5isq9">
                  <Check className="mt-0.5 h-4 w-4 flex-none text-emerald-500" strokeWidth={2.5} data-id="element-199" data-mp="yt5dhirlqq6g" />{li}
                </li>)}
            </ul>
            <p className="mt-4 border-t border-hairline2 pt-4 text-center text-sm font-bold text-emerald-400" data-id="element-200" data-mp="3jkfkjer5ditu">
              One missed $3,000 job costs you more than a whole month of this. It pays for itself the first call it saves.
            </p>
          </div>

          <div className="mt-6" data-id="element-201" data-mp="1u2inrblwwait"><GuaranteeBadge data-id="element-202" data-mp="1u54vbb8syq11" /></div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-neutral-500" data-id="element-203" data-mp="1p3pbfmid2pk8">
            <span className="inline-flex items-center gap-1.5" data-id="element-204" data-mp="1kcfmko4mzq9e"><Clock className="h-3.5 w-3.5 text-emerald-500" data-id="element-205" data-mp="1kb4kcsquaqke" /> Live in days, not months</span>
            <span className="inline-flex items-center gap-1.5" data-id="element-206" data-mp="1ga2qysjlt8qc"><Users className="h-3.5 w-3.5 text-emerald-500" data-id="element-207" data-mp="1bdu4eu32ud95" /> Only 5 new businesses onboarded per month</span>
          </div>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-208" data-mp="16cejr34ta8ra">
            <BookCTA label="Book Your Free 15-Minute Call" data-id="element-209" data-mp="11f4v3pp8y14i" />
            <CallCTA data-id="element-210" data-mp="xdt53j2gndvs" />
          </div>
        </div>
      </section>

      {/* 7. CLOSER */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-211" data-mp="2vt5woic6l28a">
        <div className="mx-auto max-w-2xl px-5 py-20 text-center sm:py-28" data-id="element-212" data-mp="2vqjmeaxwo8md">
          <h2 className="text-3xl font-extrabold leading-tight text-white sm:text-4xl" data-id="element-213" data-mp="2rrkau7ho5dly">Seriously, it's time to stop letting revenue slip.</h2>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-neutral-400" data-id="element-214" data-mp="iie51f0vgejg">
            Every call that goes to voicemail is a job your competitor answers instead. Book fifteen
            minutes, or just call us right now and hear the system work.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-215" data-mp="rcsird5r7h0g">
            <BookCTA label="Book Your Call" data-id="element-216" data-mp="3qrwbnbkza8ev" />
            <CallCTA data-id="element-217" data-mp="3vo4k664o73d2" />
          </div>
          <div className="mt-6 flex justify-center" data-id="element-218" data-mp="3vp683b6sbkc7"><GuaranteeBadge data-id="element-219" data-mp="3ldf9h28qtnm" /></div>
        </div>
      </section>

      {/* TERMS */}
      <section id="terms" className="border-b border-hairline" data-id="element-220" data-mp="3ngduphglv3y">
        <div className="mx-auto max-w-3xl px-5 py-12" data-id="element-221" data-mp="8lrp94nekdoa">
          <div className="font-eyebrow text-[10px] font-semibold uppercase text-neutral-500" data-id="element-222" data-mp="1f0saogesxdgm">Guarantee terms &amp; conditions</div>
          <div className="mt-3 space-y-2 text-[11px] leading-relaxed text-neutral-500" data-id="element-223" data-mp="1jy27wi8fmtf0">
            <p data-id="element-224" data-mp="vnuwuuvgc0zx">The 14-day money-back guarantee applies to the first month only and to missed-call response speed only. It guarantees that Viridia's system sends a response to every missed call it receives within 5 minutes during the client's first 14 days after go-live. Response times are automatically timestamped and logged; those logs are the sole record for any guarantee claim.</p>
            <p data-id="element-225" data-mp="1echzl6q5oeac">The guarantee covers missed-call response and delivery, which Viridia controls. It does not guarantee booked jobs, revenue, close rate, or number of customers, which depend on the client's own pricing, sales, and market. The 14-day window begins at go-live, once the system is active and the client has completed required onboarding.</p>
            <p data-id="element-226" data-mp="1ja9pxwb5jscf">The guarantee does not apply if the client has not completed onboarding or synced their calendar or granted required access, disconnects or revokes their phone, Yelp, Google, or CRM integrations, or pauses, disables, or routes calls outside the system. One claim per client; refund of the first month's fee is the sole and entire remedy. Any one-time setup fee is non-refundable.</p>
            <p data-id="element-227" data-mp="3f3i786034w2n">The guarantee covers responses the system sends. It does not apply where a response cannot be delivered for reasons outside Viridia's control. This includes where the caller or lead has opted out of messages, unsubscribed, blocked or reported the number, or withdrawn, archived, cancelled, or reclaimed their request before a response could be delivered, and where a carrier, lead platform, or messaging provider blocks, filters, or delays delivery. Leads in any of those categories are excluded from the guarantee calculation.</p>
            <p data-id="element-228" data-mp="2n9pz5otkagum">Service is month to month; cancel anytime with 7 days' notice. The system relies on third-party platforms (the client's phone carrier, Yelp, Google, Meta, and workflow tools); Viridia is not responsible for their outages or policy changes, and the guarantee is contingent on those integrations remaining connected. Client results shown are real past-client outcomes and are not a guarantee of the same results.</p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-5 py-10" data-id="element-229" data-mp="1x85cy0wdpe80">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center" data-id="element-230" data-mp="1x744832u53tg">
          <img src={LOGO_URL} alt="Viridia Analytics" className="h-12 w-auto opacity-80" data-id="element-231" data-mp="2206xsb7m4d9n" />
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-neutral-500" data-id="element-232" data-mp="25zz1a9i5ft85">
            <a href={PHONE_TEL} onClick={trackCallTapped} className="inline-flex items-center gap-1.5 text-emerald-300 hover:text-emerald-200" data-id="element-233" data-mp="2ayilo5h3gwye"><Phone className="h-3.5 w-3.5" data-id="element-234" data-mp="2fxc1w5nrn229" /> {PHONE_DISPLAY}</a>
            <span className="inline-flex items-center gap-1.5" data-id="element-235" data-mp="2kwyb840h3y5s"><Mail className="h-3.5 w-3.5" data-id="element-236" data-mp="ll5gyf52l6wr" /> info@viridiaanalytics.com</span>
            <span className="inline-flex items-center gap-1.5" data-id="element-237" data-mp="ln8gbps44ce5"><MapPin className="h-3.5 w-3.5" data-id="element-238" data-mp="qjgqeorgdfeg" /> Phoenix, AZ</span>
          </div>
          <a href="https://viridiaanalytics.com" target="_blank" rel="noopener noreferrer" className="text-xs text-neutral-600 underline decoration-neutral-800 underline-offset-4 hover:text-neutral-400" data-id="element-239" data-mp="33u9sfg0auqu3">See the full Viridia Analytics site</a>
        </div>
      </footer>
    </div>;
}